import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, ChevronRight, Lock, ArrowLeft, Building2, Calendar, Users, Target, Sparkles, Zap, TrendingUp, User, Briefcase, CheckCircle, Gift, History } from 'lucide-react';
import { Button } from '../components/ui/button';
import SiteHeader from '../components/SiteHeader';
import SiteBackground from '../components/SiteBackground';
import api from '../utils/api';

const DynamicPage = () => {
  const { path, subpath } = useParams();
  const [pageData, setPageData] = useState(null);
  const [navigationItems, setNavigationItems] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [recruitmentItems, setRecruitmentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFooterMenu, setExpandedFooterMenu] = useState(null); // 展開中のフッターメニュー

  const companyHistory = [
    { date: '2002年7月', text: '代表取締役 黒崎 龍一が会社を設立し、一般労働者派遣事業を開始' },
    { date: '2016年3月', text: '地域生活支援事業を開始' },
    { date: '2017年4月', text: '居住支援事業を開始' },
    { date: '2021年4月', text: '就労継続支援B型 海風事業所を開始' },
    { date: '2024年4月', text: '就労継続支援B型 アトリエいずみ事業所を開始' },
    { date: '2025年7月', text: '鳥飼 巧が代表取締役社長に就任、黒崎 龍一が会長に就任' },
    { date: '2025年10月', text: '福祉関連ソフトの開発を開始' },
    { date: '2026年', text: '現在に至る' },
  ];
  const parseYearMonth = (s) => {
    const m = String(s || '').match(/(\d+)\s*年(?:\s*(\d+)\s*月)?/);
    if (!m) return Number.MAX_SAFE_INTEGER;
    return parseInt(m[1], 10) * 100 + (m[2] ? parseInt(m[2], 10) : 0);
  };
  const rawHistory = (companyInfo?.history && companyInfo.history.length > 0) ? companyInfo.history : companyHistory;
  const historyList = [...rawHistory].sort((a, b) => parseYearMonth(a.date) - parseYearMonth(b.date));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [navResponse, companyResponse] = await Promise.all([
          api.get('/navigation'),
          api.get('/company-info'),
        ]);
        setNavigationItems(navResponse.data);
        setCompanyInfo(companyResponse.data);
        
        // パスに一致するページを探す（サブパスがある場合も対応）
        const fullPath = subpath ? `/${path}/${subpath}` : `/${path}`;
        const page = navResponse.data.find(item => item.path === fullPath);
        setPageData(page);

        // 採用情報ページは採用情報管理の求人一覧を表示
        if (page && page.path === '/recruit') {
          try {
            const rec = await api.get('/recruitment/active');
            setRecruitmentItems(rec.data);
          } catch (e) {
            // 取得失敗時は空
          }
        }
      } catch (error) {
        console.error('ページデータの取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [path, subpath]);

  // Markdownの簡易パーサー
  const renderMarkdown = (text) => {
    if (!text) return '';
    
    return text
      .split('\n')
      .map((line, index) => {
        // 見出し
        if (line.startsWith('# ')) {
          return <h1 key={index} className="text-4xl font-bold mb-6 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">{line.substring(2)}</h1>;
        }
        if (line.startsWith('## ')) {
          return <h2 key={index} className="text-3xl font-bold mb-4 text-gray-900 mt-8">{line.substring(3)}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={index} className="text-2xl font-bold mb-3 text-amber-500 mt-6">{line.substring(4)}</h3>;
        }
        
        // リスト
        if (line.startsWith('- ')) {
          return <li key={index} className="text-gray-600 ml-6 mb-2">{line.substring(2)}</li>;
        }
        if (/^\d+\.\s/.test(line)) {
          return <li key={index} className="text-gray-600 ml-6 mb-2 list-decimal">{line.replace(/^\d+\.\s/, '')}</li>;
        }
        
        // 太字
        const boldText = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-amber-500">$1</strong>');
        
        // 空行
        if (line.trim() === '') {
          return <br key={index} />;
        }
        
        // 通常の段落
        return <p key={index} className="text-gray-600 mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: boldText }}></p>;
      });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center">
        <div className="text-amber-500">読み込み中...</div>
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">ページが見つかりません</h1>
          <Link to="/">
            <Button className="bg-gradient-to-r from-amber-500 to-amber-600">
              <ArrowLeft className="w-4 h-4 mr-2" />
              ホームに戻る
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-gray-900 relative">
      <SiteBackground />
      <SiteHeader activePath={subpath ? `/${path}/${subpath}` : `/${path}`} />

      {/* コンテンツ */}
      <main className="pt-40 pb-20 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-amber-500 hover:text-amber-500 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            ホームに戻る
          </Link>
          
          <div className="bg-gradient-to-br from-gray-50/50 to-gray-100/50 border border-amber-500/20 rounded-2xl p-8 md:p-12 backdrop-blur-sm">
            {/* 写真がある場合は表示 */}
            {pageData.image_url && (
              <div className="flex justify-center mb-8">
                <img 
                  src={pageData.image_url} 
                  alt={pageData.label}
                  className="w-32 h-32 rounded-full object-cover border-4 border-amber-500/30 shadow-lg"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
            
            {pageData.path === '/recruit' ? (
              <div className="space-y-8">
                <div className="text-center mb-4">
                  <div className="flex items-center justify-center mb-4">
                    <Briefcase className="w-12 h-12 text-amber-500" />
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold mb-3 text-amber-500">採用情報</h1>
                  <p className="text-gray-600">私たちと一緒に働きませんか？</p>
                </div>
                {recruitmentItems.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">現在、募集中の求人はありません。</p>
                ) : (
                  <div className="grid md:grid-cols-2 gap-8">
                    {recruitmentItems.map((job) => (
                      <div key={job.id} className="group relative rounded-2xl bg-gradient-to-br from-gray-50/80 to-gray-100/80 border border-amber-500/20 backdrop-blur-sm overflow-hidden hover:border-amber-500/50 transition-all duration-500" data-testid={`recruit-card-${job.id}`}>
                        <div className="relative p-8">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-2xl font-bold text-gray-900 group-hover:text-amber-500 transition-colors">{job.title}</h3>
                            <span className="px-3 py-1 bg-green-500/20 text-green-500 text-xs rounded-full border border-green-500/30">募集中</span>
                          </div>
                          <div className="rich-content text-gray-600 mb-6" dangerouslySetInnerHTML={{ __html: job.description }} />
                          <div className="space-y-4 mb-6">
                            {job.location && (
                              <div className="flex items-start space-x-3">
                                <MapPin className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                                <div><h4 className="text-sm font-semibold text-amber-500 mb-1">勤務地</h4><p className="text-gray-600">{job.location}</p></div>
                              </div>
                            )}
                            {job.employment_type && (
                              <div className="flex items-start space-x-3">
                                <Briefcase className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                                <div><h4 className="text-sm font-semibold text-amber-500 mb-1">雇用形態</h4><p className="text-gray-600">{job.employment_type}</p></div>
                              </div>
                            )}
                            {job.requirements && (
                              <div className="flex items-start space-x-3">
                                <CheckCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                                <div><h4 className="text-sm font-semibold text-amber-500 mb-1">応募資格</h4><div className="rich-content text-gray-600 text-sm" dangerouslySetInnerHTML={{ __html: job.requirements }} /></div>
                              </div>
                            )}
                            {job.benefits && (
                              <div className="flex items-start space-x-3">
                                <Gift className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                                <div><h4 className="text-sm font-semibold text-amber-500 mb-1">待遇・福利厚生</h4><div className="rich-content text-gray-600 text-sm" dangerouslySetInnerHTML={{ __html: job.benefits }} /></div>
                              </div>
                            )}
                          </div>
                          <Link to={`/apply?job_id=${job.id}&job_title=${encodeURIComponent(job.title)}`} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                            <Button className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-gray-900 shadow-lg shadow-amber-500/30" data-testid={`recruit-apply-${job.id}`}>
                              この求人に応募する
                              <ChevronRight className="ml-2 w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : pageData.content ? (
              pageData.path === '/company' && companyInfo ? (
                // 会社概要ページ専用レイアウト
                <div className="space-y-12">
                  {/* タイトル */}
                  <div className="text-center mb-12">
                    <div className="flex items-center justify-center mb-4">
                      <Building2 className="w-12 h-12 text-amber-500" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                      会社概要
                    </h1>
                    <p className="text-gray-600">株式会社プレサージュの会社概要をご紹介します。</p>
                  </div>

                  {/* 会社概要 */}
                  <div className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 border border-amber-500/30 rounded-lg p-8">
                    <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                      <Building2 className="w-6 h-6 mr-3 text-amber-500" />
                      会社概要
                    </h2>
                    <div className="grid gap-6">
                      <div className="flex items-start space-x-4">
                        <Building2 className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-gray-500">会社名</p>
                          <p className="text-gray-900 font-semibold">{companyInfo.name}</p>
                        </div>
                      </div>
                      {companyInfo.representative_director && (
                        <div className="flex items-start space-x-4">
                          <User className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-gray-500">代表取締役</p>
                            <p className="text-gray-900 font-semibold">{companyInfo.representative_director}</p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-start space-x-4">
                        <MapPin className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-gray-500">所在地</p>
                          <p className="text-gray-900">{companyInfo.address}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-4">
                        <Phone className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-gray-500">電話・FAX</p>
                          <p className="text-gray-900">{companyInfo.phone} / FAX: {companyInfo.fax}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-4">
                        <Mail className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-gray-500">メールアドレス</p>
                          <p className="text-gray-900">{companyInfo.email}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-4">
                        <Clock className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-gray-500">営業時間</p>
                          <p className="text-gray-900">{companyInfo.business_hours}</p>
                        </div>
                      </div>
                      {companyInfo.established && (
                        <div className="flex items-start space-x-4">
                          <Calendar className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-gray-500">設立</p>
                            <p className="text-gray-900">{companyInfo.established}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 企業理念 */}
                  <div className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 border border-amber-500/30 rounded-lg p-8">
                    <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                      <Target className="w-6 h-6 mr-3 text-amber-500" />
                      企業理念
                    </h2>
                    <div className="space-y-4">
                      <p className="text-gray-900 text-lg font-semibold">
                        {companyInfo.philosophy_title || 'より良いこれからを創造する会社です。'}
                      </p>
                      <p className="text-gray-600 whitespace-pre-line">
                        {companyInfo.philosophy_description || '障がいの有無に関わらず、すべての人が自分の能力を最大限に発揮し、社会に貢献できる環境を創造することが私たちの使命です。'}
                      </p>
                    </div>
                  </div>

                  {/* 私たちの価値観 */}
                  {companyInfo.values && companyInfo.values.length > 0 && (
                    <div className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 border border-amber-500/30 rounded-lg p-8">
                      <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                        <Users className="w-6 h-6 mr-3 text-amber-500" />
                        私たちの価値観
                      </h2>
                      <div className="grid md:grid-cols-2 gap-6">
                        {companyInfo.values.map((value, index) => {
                          const ValueIcon = [Sparkles, Users, Zap, TrendingUp][index % 4];
                          return (
                            <div key={index} className="flex items-start space-x-4">
                              <ValueIcon className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
                              <div>
                                <p className="text-gray-900 font-semibold mb-2">{value.title}</p>
                                <p className="text-gray-600 text-sm">{value.description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* 沿革 HISTORY */}
                  <div className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 border border-amber-500/30 rounded-lg p-8" data-testid="company-history-section">
                    <h2 className="text-2xl font-bold mb-10 text-gray-900 flex items-center">
                      <History className="w-6 h-6 mr-3 text-amber-500" />
                      沿革
                    </h2>

                    {/* デスクトップ: 横型タイムライン */}
                    <div className="hidden md:block">
                      <div className="relative w-full px-1">
                        <div className="absolute top-2 left-0 right-0 h-0.5 bg-gray-300" />
                        <div className="relative grid" style={{ gridTemplateColumns: `repeat(${historyList.length}, minmax(0, 1fr))` }}>
                          {historyList.map((item, index) => {
                            const isEnd = index === 0 || index === historyList.length - 1;
                            return (
                              <div key={index} className="flex flex-col items-center text-center px-1" data-testid={`history-item-${index}`}>
                                <span className={`w-4 h-4 rounded-full ring-4 ring-white ${isEnd ? 'bg-amber-500' : 'bg-gray-900'}`} />
                                <p className="mt-4 text-xs font-bold text-gray-900">{item.date}</p>
                                <p className="mt-2 text-[11px] text-gray-600 leading-relaxed whitespace-pre-line">{item.text}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* モバイル: 縦型タイムライン */}
                    <div className="md:hidden relative pl-6">
                      <div className="absolute top-1 bottom-1 left-[7px] w-0.5 bg-gray-300" />
                      <div className="space-y-6">
                        {historyList.map((item, index) => {
                          const isEnd = index === 0 || index === historyList.length - 1;
                          return (
                            <div key={index} className="relative" data-testid={`history-item-mobile-${index}`}>
                              <span className={`absolute -left-6 top-1 w-4 h-4 rounded-full ring-4 ring-white ${isEnd ? 'bg-amber-500' : 'bg-gray-900'}`} />
                              <p className="text-sm font-bold text-gray-900">{item.date}</p>
                              <p className="mt-1 text-sm text-gray-600 leading-relaxed whitespace-pre-line">{item.text}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* アクセス */}
                  <div className="bg-gradient-to-br from-gray-50/80 to-gray-100/80 border border-amber-500/30 rounded-lg p-8" data-testid="company-access-section">
                    <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                      <MapPin className="w-6 h-6 mr-3 text-amber-500" />
                      アクセス
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6 items-start">
                      <div className="space-y-4">
                        <div className="flex items-start space-x-4">
                          <MapPin className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-gray-500">所在地</p>
                            <p className="text-gray-900">{companyInfo.address}</p>
                          </div>
                        </div>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(companyInfo.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="company-access-directions-link"
                          className="inline-flex items-center px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm font-semibold shadow-lg shadow-amber-500/30 hover:from-amber-600 hover:to-amber-700 transition-colors"
                        >
                          <MapPin className="w-4 h-4 mr-2" />
                          Googleマップで経路を見る
                        </a>
                      </div>
                      <div className="rounded-lg overflow-hidden border border-amber-500/20 shadow-sm">
                        <iframe
                          title="アクセスマップ"
                          data-testid="company-access-map"
                          src={`https://www.google.com/maps?q=${encodeURIComponent(companyInfo.address)}&output=embed`}
                          width="100%"
                          height="320"
                          style={{ border: 0 }}
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // 通常ページ（HTMLならそのまま表示、旧Markdownは従来通り）
                <div className="prose max-w-none">
                  {/<[a-z][\s\S]*>/i.test(pageData.content || '') ? (
                    <div className="rich-content text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: pageData.content }} />
                  ) : (
                    renderMarkdown(pageData.content)
                  )}
                </div>
              )
            ) : (
              <div className="text-center py-12">
                <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                  {pageData.label}
                </h1>
                <p className="text-gray-600">このページのコンテンツは準備中です。</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* フッター */}
      <footer className="relative border-t border-amber-500/20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            {/* お問い合わせ */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                お問い合わせ
              </h3>
              {companyInfo ? (
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 text-gray-600 hover:text-amber-500 transition-colors">
                    <MapPin className="w-5 h-5 mt-1 flex-shrink-0 text-amber-500" />
                    <span>{companyInfo.address}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-600 hover:text-amber-500 transition-colors">
                    <Phone className="w-5 h-5 flex-shrink-0 text-amber-500" />
                    <span>{companyInfo.phone} {companyInfo.fax && `FAX ${companyInfo.fax}`}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-600 hover:text-amber-500 transition-colors">
                    <Mail className="w-5 h-5 flex-shrink-0 text-amber-500" />
                    <span>{companyInfo.email}</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-600 hover:text-amber-500 transition-colors">
                    <Clock className="w-5 h-5 flex-shrink-0 text-amber-500" />
                    <span>{companyInfo.business_hours}</span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">読み込み中...</p>
              )}
            </div>

            {/* メニュー */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                メニュー
              </h3>
              <ul className="space-y-3">
                {navigationItems
                  .filter(item => !item.parent_id)
                  .map((item) => {
                    const subItems = navigationItems.filter(sub => sub.parent_id === item.id);
                    const hasSubMenu = subItems.length > 0;
                    const isExpanded = expandedFooterMenu === item.id;
                    
                    return (
                      <li key={item.id}>
                        {hasSubMenu ? (
                          // サブメニューがある場合：クリックで展開
                          <button
                            onClick={() => setExpandedFooterMenu(isExpanded ? null : item.id)}
                            className="text-gray-600 hover:text-amber-500 transition-colors duration-300 flex items-center group w-full text-left"
                          >
                            <ChevronRight className={`w-4 h-4 mr-2 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} />
                            {item.label}
                          </button>
                        ) : (
                          // サブメニューがない場合：通常のリンク
                          <Link
                            to={item.path}
                            className="text-gray-600 hover:text-amber-500 transition-colors duration-300 flex items-center group"
                          >
                            <ChevronRight className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" />
                            {item.label}
                          </Link>
                        )}
                        
                        {/* サブメニュー */}
                        {hasSubMenu && isExpanded && (
                          <ul className="ml-6 mt-2 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                            {subItems.map((subItem) => (
                              <li key={subItem.id}>
                                <Link
                                  to={subItem.path}
                                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                  className="text-gray-500 hover:text-amber-500 transition-colors duration-300 flex items-center group text-sm"
                                >
                                  <ChevronRight className="w-3 h-3 mr-2 group-hover:translate-x-1 transition-transform" />
                                  {subItem.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
              </ul>
            </div>

            {/* 最新情報 */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                最新情報
              </h3>
              <p className="text-gray-600 text-sm">
                最新のニュースやお知らせは<Link to="/" className="text-amber-500 hover:text-amber-500 underline">ホームページ</Link>をご覧ください。
              </p>
            </div>
          </div>

          {/* コピーライトと管理者ログイン */}
          <div className="border-t border-amber-500/20 pt-8">
            <div className="flex justify-between items-center">
              <p className="text-gray-600 text-sm">
                Copyright © 2002 {companyInfo?.name || '株式会社プレサージュ'}
              </p>
              <Link to="/admin/login">
                <Button 
                  variant="ghost" 
                  className="text-gray-500 hover:text-amber-500 text-sm"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  管理者ログイン
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DynamicPage;
